import Phaser from 'phaser';
import { SCENES } from '../types/game';
import type { GameStats } from '../types/game';

export class ResultsScene extends Phaser.Scene {
  private userData: any = null;
  private stats: GameStats = {} as GameStats;
  private gameSeed: string = '';
  private duration: number = 0;
  private maxCps: number = 0;
  private submitted: boolean = false;
  private isClaiming: boolean = false;

  constructor() {
    super({ key: SCENES.RESULTS });
  }

  init(data?: any) {
    if (data) {
      this.userData = data.user;
      this.stats = data.stats;
      this.gameSeed = data.seed;
      this.duration = data.duration;
      this.maxCps = data.maxCps;
    }
    this.submitted = false;
    this.isClaiming = false;
  }

  create() {
    const { width, height } = this.cameras.main;

    this.add.image(width / 2, height / 2, 'background');

    const title = this.add.text(width / 2, 80, 'GAME OVER', {
      fontFamily: 'Minecraft',
      fontSize: '48px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    const survivalTime = Math.floor(this.duration / 1000);
    const timeText = this.add.text(width / 2, 140, `Survived: ${survivalTime} seconds`, {
      fontFamily: 'Minecraft',
      fontSize: '24px',
      color: '#a3a3a3',
    });
    timeText.setOrigin(0.5);

    if (this.userData && this.userData.address) {
      const userText = this.add.text(
        width / 2,
        170,
        `Hero: ${this.userData.address.slice(0, 6)}...${this.userData.address.slice(-4)}`,
        {
          fontFamily: 'Minecraft',
          fontSize: '14px',
          color: '#4ade80',
          fontStyle: 'bold',
        }
      );
      userText.setOrigin(0.5);
    }

    this.createStatsPanel();
    this.createButtons();

    if (this.userData && this.userData.address && !this.submitted) {
      this.submitScore();
    }
  }

  private createStatsPanel() {
    const { width } = this.cameras.main;
    const panelY = 200;

    const panel = this.add.graphics();
    panel.fillStyle(0x1e1b4b, 0.8);
    panel.fillRoundedRect(width / 2 - 200, panelY, 400, 220, 10);

    const stats = [
      `Final Score: ${this.stats.score}`,
      `Enemies KILLED: ${this.stats.kills}`,
      `Accuracy: ${this.stats.accuracy.toFixed(1)}%`,
      `Max Combo: ${this.stats.maxCombo}`,
      `Waves Survived: ${this.stats.wave}`,
      `Shots Fired: ${this.stats.shotsFired}`,
    ];

    stats.forEach((stat, index) => {
      this.add
        .text(width / 2, panelY + 30 + index * 30, stat, {
          fontFamily: 'Minecraft',
          fontSize: '18px',
          color: '#ffffff',
        })
        .setOrigin(0.5);
    });
  }

  private createButtons() {
    const { width, height } = this.cameras.main;

    const playAgainButton = this.add.image(width / 2, height - 120, 'button').setInteractive({ cursor: 'pointer' });
    this.add
      .text(width / 2, height - 120, 'PLAY AGAIN', {
        fontSize: '20px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    playAgainButton.on('pointerdown', () => this.scene.start(SCENES.HOME, { user: this.userData }));
    playAgainButton.on('pointerover', () => playAgainButton.setTint(0x8b5cf6));
    playAgainButton.on('pointerout', () => playAgainButton.clearTint());

    const leaderboardButton = this.add.image(width / 2, height - 60, 'leaderboard_btn').setScale(0.8).setInteractive({ cursor: 'pointer' });
    leaderboardButton.on('pointerdown', () => this.scene.start(SCENES.LEADERBOARD, { user: this.userData }));
    leaderboardButton.on('pointerover', () => leaderboardButton.setScale(0.9));
    leaderboardButton.on('pointerout', () => leaderboardButton.setScale(0.8));

    if (!this.userData || !this.userData.address) {
      this.add
        .text(width / 2, height - 200, 'Sign in with Privy to submit score on-chain!', {
          fontFamily: 'Minecraft',
          fontSize: '16px',
          color: '#fbbf24',
          align: 'center',
        })
        .setOrigin(0.5);
    }
  }

  private async submitScore() {
    if (this.submitted || !this.userData || !this.userData.address) return;
    this.submitted = true;

    const { width } = this.cameras.main;
    const statusText = this.add
      .text(width / 2, 450, 'Ready to submit your score on Somnia.', {
        fontSize: '18px',
        color: '#fbbf24',
      })
      .setOrigin(0.5);

    if (this.stats.score <= 0 && this.stats.kills <= 0) {
      statusText.setText('No score to submit this round.').setColor('#a3a3a3');
      return;
    }

    const submitButton = this.add.rectangle(width / 2, 500, 260, 40, 0x2563eb, 1).setInteractive({ cursor: 'pointer' });
    submitButton.setStrokeStyle(2, 0x93c5fd);

    const submitText = this.add
      .text(width / 2, 500, 'SUBMIT SCORE ON-CHAIN', {
        fontSize: '16px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 535, 'Somnia mode: backend signs, player sends tx', {
        fontFamily: 'Minecraft',
        fontSize: '13px',
        color: '#a3a3a3',
      })
      .setOrigin(0.5);

    submitButton.on('pointerover', () => submitButton.setFillStyle(0x1d4ed8));
    submitButton.on('pointerout', () => submitButton.setFillStyle(0x2563eb));

    submitButton.on('pointerdown', async () => {
      if (this.isClaiming) return;
      this.isClaiming = true;

      submitText.setText('SUBMITTING...');
      submitButton.setFillStyle(0x1e3a8a);

      try {
        this.events.emit('executeSTTClaim', {
          playerAddress: this.userData.address,
          scoreDelta: this.stats.score,
          killsDelta: this.stats.kills,
          durationMs: this.duration,
          seed: this.gameSeed || `${this.userData.address}-${Date.now()}`,
          timestamp: Date.now(),
          onSuccess: () => {
            submitText.setText('SCORE SUBMITTED');
            submitButton.setFillStyle(0x059669);
            submitButton.disableInteractive();
            statusText.setText('Score submitted successfully to Somnia.').setColor('#4ade80');
          },
          onError: (err: any) => {
            this.isClaiming = false;
            submitText.setText('TRY AGAIN');
            submitButton.setFillStyle(0xdc2626);
            statusText.setText('Failed to submit score. Please retry.').setColor('#ef4444');
            console.error('Score submission failed', err);
          },
        });
      } catch (error) {
        this.isClaiming = false;
        submitText.setText('ERROR - RETRY');
        submitButton.setFillStyle(0xdc2626);
        statusText.setText('Unexpected error while preparing transaction.').setColor('#ef4444');
        console.error('Unexpected submitScore error', error);
      }
    });
  }
}
